import "./Modal.scss";

import clsx from "clsx";
import type { MouseEvent, ReactNode } from "react";
import { forwardRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
// eslint-disable-next-line no-restricted-imports
import { Provider as ReduxStoreProvider } from "react-redux";

import { CloseButton } from "./CloseButton";
import { SquareButton, type SquareButtonProps } from "./buttons/SquareButton";

import { t } from "i18n/i18n";
import { ShareDBContext, type ShareDBContextType } from "providers/ShareDBProvider";
import { store } from "store/index";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { BrowserRouter } from "utils/routing";

export type ModalProps<T> = {
  fullScreen?: boolean;
  children?:
    | ((
        onClose: (data?: T) => void | undefined,
        onConfirm: (data?: T) => void | undefined,
      ) => ReactNode)
    | ReactNode;
  className?: string;
  disableConfirmButton?: boolean;
  onConfirm?: (data?: T) => void;
  onCancel?: (data?: T) => void;
  onClose?: (data?: T) => void;
  hideCloseButton?: boolean;
  hideCancelConfirmButtons?: boolean;
  hideCancelButton?: boolean;
  allowManualClose?: boolean;
  title?: ReactNode;
  labelText?: ReactNode;
  cancelButtonText?: ReactNode;
  confirmButtonText?: ReactNode;
  confirmButtonKind?: "accent" | "caution";
  confirmButtonProps?: SquareButtonProps;
  shareDBContextValue?: ShareDBContextType | null;
  isWarningLabel?: boolean;
};

// eslint-disable-next-line react-refresh/only-export-components
export function showModalWithProviders(render: (unmount: () => void) => ReactNode) {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = createRoot(container);

  const unmount = () => {
    root.unmount();
    if (container.parentNode) container.parentNode.removeChild(container);
  };

  root.render(
    <ReduxStoreProvider store={store}>
      <BrowserRouter>
        <ShareDBContext.Provider value={null}>{render(unmount)}</ShareDBContext.Provider>
      </BrowserRouter>
    </ReduxStoreProvider>,
  );

  return unmount;
}

// eslint-disable-next-line react-refresh/only-export-components
export const showConfirmationModal = <T,>({
  onClose: customClose,
  onConfirm: customConfirm,
  onCancel: customCancel,
  allowManualClose = true,
  className,
  shareDBContextValue,
  ...modalProps
}: ModalProps<T>): Promise<T | boolean> => {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = createRoot(container);

  const confirmation = new Promise<T | boolean>((resolve) => {
    const onConfirm = (data?: T) => {
      if (customConfirm) {
        customConfirm(data);
      }

      trackEvent("Confirmation modal - Confirm", BUTTON_EVENT_CATEGORY);

      resolve(data || true);
    };

    const onClose = (data?: T) => {
      trackEvent("Confirmation modal - Close", BUTTON_EVENT_CATEGORY);

      if (customClose) {
        customClose(data);
      }

      resolve(false);
    };

    const onCancel = customCancel
      ? (data?: T) => {
          trackEvent("Confirmation modal - Cancel", BUTTON_EVENT_CATEGORY);

          if (customCancel) {
            customCancel(data);
          }

          resolve(false);
        }
      : onClose;

    root.render(
      <ReduxStoreProvider store={store}>
        <BrowserRouter>
          <ShareDBContext.Provider value={shareDBContextValue ?? null}>
            <div
              className="modal-backdrop"
              onClick={allowManualClose ? () => onClose() : undefined}
            />
            <Modal
              allowManualClose={allowManualClose}
              className={clsx(className, "confirm-modal")}
              onCancel={onCancel}
              onClose={onClose}
              onConfirm={onConfirm}
              {...modalProps}
            />
          </ShareDBContext.Provider>
        </BrowserRouter>
      </ReduxStoreProvider>,
    );
  });

  confirmation.finally(() => {
    root.unmount();
    document.body.removeChild(container);
  });
  return confirmation;
};

const ignoreClick = (event: MouseEvent<HTMLDivElement>) => {
  event.stopPropagation();
};

// This is a very basic modal.  As we require more features, we can pull things from:
// https://github.com/minervaproject/picasso/blob/development/forum-design-system/src/components/modal.tsx

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Modal = forwardRef<HTMLDivElement, ModalProps<any>>(
  (
    {
      children,
      className,
      fullScreen,
      onClose,
      onConfirm,
      onCancel,
      allowManualClose = true,
      disableConfirmButton = false,
      hideCloseButton = false,
      hideCancelConfirmButtons = false,
      hideCancelButton = false,
      cancelButtonText = t("common.cancel"),
      confirmButtonText = t("common.confirm"),
      confirmButtonKind = "accent",
      confirmButtonProps,
      title,
      labelText,
      isWarningLabel = false,
    },
    ref,
  ) => {
    useEffect(() => {
      const closeOnEsc = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          onClose?.();
        }
      };

      if (allowManualClose && onClose) {
        window.addEventListener("keydown", closeOnEsc);
      }

      return () => window.removeEventListener("keydown", closeOnEsc);
    }, [onClose, allowManualClose]);

    return createPortal(
      <div
        aria-modal="true"
        className={clsx(
          className,
          "modal fixed left-1/2 top-1/2 z-[var(--modal-z)] -translate-x-1/2 -translate-y-1/2 rounded-[12px] border border-black-tint-90 bg-white p-6 shadow-modal supports-squircle:rounded-[24px] supports-squircle:corner-squircle",
          { "modal--full-screen": fullScreen },
        )}
        ref={ref}
        role="dialog"
        onClick={ignoreClick}
      >
        {!hideCloseButton && <CloseButton className="absolute right-3 top-3" onClick={onClose} />}

        {title && (
          <h2
            className={clsx(
              "mb-4 text-base font-medium text-black-tint-10",
              !hideCloseButton && "pr-[46px]",
            )}
          >
            {title}
          </h2>
        )}

        {labelText && (
          <div
            className={clsx(
              "modal-label body-s font-normal text-black-tint-20",
              isWarningLabel && "border-l-2 border-red-tint-20 pl-4",
            )}
          >
            {labelText}
          </div>
        )}

        <div className="modal-body body-s font-normal text-black-tint-20">
          {typeof children === "function"
            ? children(onClose ?? (() => {}), onConfirm ?? (() => {}))
            : children}
        </div>

        {onConfirm && !hideCancelConfirmButtons && (
          <div className="mt-8 flex justify-end gap-2">
            {!hideCancelButton && (
              <SquareButton
                kind="tertiary"
                size="m"
                title={typeof cancelButtonText === "string" ? cancelButtonText : undefined}
                onClick={onCancel}
              >
                {cancelButtonText}
              </SquareButton>
            )}
            <SquareButton
              disabled={disableConfirmButton}
              kind={confirmButtonKind}
              size="m"
              title={typeof confirmButtonText === "string" ? confirmButtonText : undefined}
              onClick={onConfirm}
              {...confirmButtonProps}
            >
              {confirmButtonText}
            </SquareButton>
          </div>
        )}
      </div>,
      document.body,
    );
  },
);

Modal.displayName = "Modal";
